import { Injectable } from '@angular/core';
import { User } from '../models/user-model';
import { URLS } from '../shared/url';
import { Router, Params } from '@angular/router';
import { BaseHttpService } from './base-http.service';
import { Observable } from 'rxjs';
import { Response, ProfileViewModel, TokenResult } from '../models/generated-view-models';
import { ResponseTypeEnum } from '../models/enums';
import { UserProfileService } from './user-profile.service';
import { Globals } from '../shared/globals';

interface ISecureLinkParams extends Params {
    token?: string;
}

interface ISecureLinkResult {
    isValid: boolean;
    email: string;
}

export interface IResetUserPasswordViewModel {
    UserName: string;
    NewPassword: string;
    Token: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    private _user: ProfileViewModel = new ProfileViewModel();
    public redirectUrl: string;

    constructor(
        private baseHttp: BaseHttpService,
        private router: Router,
        private profileService: UserProfileService,
        private globals: Globals) {}

    public get isAuthorized(): boolean {
        return !!localStorage.getItem('Authorization');
    }
    
    public get user(): ProfileViewModel {
        return this._user;
    }

    public set user(value: ProfileViewModel) {
        this._user = value;
    }

    public login = (user: User): Observable<Response<TokenResult>> => {
        return this.baseHttp.Post(URLS.login, user, ResponseTypeEnum.Full);   
    }

    public logout = (): Observable<boolean> => {
        return this.baseHttp.Post(URLS.logout, null, ResponseTypeEnum.Success);
    }

    public checkLogin = (url: string): Observable<boolean> | boolean => {
        if (this.globals.isLoogedIn) {
            return true;
        } else {
            if (!this.isAuthorized) {
                this.router.navigate(['login']);
                return false;
            }
            return Observable.create((observer: any) => {
                this.baseHttp.Post(URLS.tokenValidation, null, ResponseTypeEnum.Full).subscribe((res: Response<boolean>) => {
                    if (res.result) {
                        this.getUserProfile();
                        this.globals.isLoogedIn = true;   
                        url && this.router.navigate([url]);     
                        observer.next(true);
                    } else {
                        this.globals.isLoogedIn = false;
                        this.router.navigate(['login']);
                        observer.next(false);
                    }
                    observer.complete();
                }, (err) => {
                    this.globals.isLoogedIn = false;
                    this.router.navigate(['login']);
                    observer.next(false);
                    observer.complete();
                });
            });
        }
    }
    
    public getUserProfile = (): void => {
        this.profileService.getUserProfile().subscribe((res: ProfileViewModel) => {
            this.user = res;
        })
    }

    public checkLogout = (): Observable<boolean> | boolean => {
        if (this.globals.isLoogedIn) {
            this.router.navigate(['']);
            return false;
        } else {
            if (!this.isAuthorized) {
                return true;
            }
            return Observable.create((observer: any) => {
                this.baseHttp.Post(URLS.tokenValidation, null, ResponseTypeEnum.Full).subscribe((res: Response<boolean>) => {
                    if (res.result) {
                        this.globals.isLoogedIn = true;
                        this.router.navigate(['']);
                        observer.next(false);
                    } else {
                        this.globals.isLoogedIn = false;
                        observer.next(true);
                    }
                    observer.complete();
                }, (err) => {
                    this.globals.isLoogedIn = false;
                    observer.next(true);
                    observer.complete();
                });
            });   
        }
    }

    public checkPasswordResetAviability = (params: ISecureLinkParams): Observable<boolean> | boolean => {
        if (params && params.token) {
            return Observable.create((observer: any) => {
                this.baseHttp.Post(`${URLS.validateSecureLinkToken}/${params.token}`, null, ResponseTypeEnum.Full).subscribe((res: Response<ISecureLinkResult>) => {
                    if(res.success && res.result.isValid) {
                        observer.next(true);
                    } else {
                        this.router.navigate(['login']);
                        observer.next(false);
                    }      
                    observer.complete();
                }, (err) => {    
                    this.router.navigate(['login']);
                    observer.next(false);
                    observer.complete();
                });
            });   
        } else {
            this.router.navigate(['login']);
            return false;
        }
    }

    public ResetPassword = (request: IResetUserPasswordViewModel): Observable<boolean> => {
        return this.baseHttp.Post(URLS.resetPassword, request, ResponseTypeEnum.Full);
    }
}
